import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { FaroButtonContained } from "@components/common/faro-button-contained";
import { AutoCompleteMessage } from "@components/common/faro-text-field/faro-text-field-message";
import { MembersAutocomplete } from "@components/common/members-autocomplete/members-autocomplete";
import {
  AutoCompleteMemberOption,
  AutoCompleteTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-types";
import {
  createMemberOption,
  createTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-utils";
import { ProjectRoleAutoComplete } from "@components/role/role-autocomplete/project-role-autocomplete";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { RoleDisplayNames } from "@custom-types/member-types";
import { BaseProjectProps } from "@custom-types/project-types";
import { Dialog } from "@flat-ui";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { useTeamUtils } from "@hooks/use-team-utils";
import { Stack } from "@mui/material";
import { useAppParams } from "@router/router-helper";
import { INVITE_DIALOG_CLOSE_DELAY_MS } from "@src/constants/invite-constants";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { EmailUtils } from "@stellar/utils";
import { setOne } from "@store/members/members-slice";
import { convertToMemberTypes } from "@store/members/members-slice-utils";
import { projectMembersSelector } from "@store/projects/projects-selector";
import {
  addProcessingProjects,
  removeProcessingProjects,
  setOneProjects,
} from "@store/projects/projects-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { teamsSelector } from "@store/teams/teams-selector";
import { isTeam } from "@utils/team-utils";
import { delay } from "@utils/time-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";

/** Renders the invite member button and the functionality in project overview */
export function InviteMemberToProject({
  project,
}: BaseProjectProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorContext();

  const companyMembers = useCompanyMembers();
  const projectMembers = useAppSelector(projectMembersSelector);
  const teams = useAppSelector(teamsSelector);
  const { isTeamName, getTeamIdByName } = useTeamUtils();

  const { companyId, projectId } = useAppParams();
  const { respondToUser } = useRespondToInvite();
  const { trackEvent } = useTrackEvent();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  /** Flag whether the input field in the autocomplete component has a valid email */
  const [isMemberInputValid, setIsMemberInputValid] = useState<boolean>(false);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [message, setMessage] = useState<AutoCompleteMessage | undefined>();

  const [selectedRole, setSelectedRole] =
    useState<FaroCoreApiTypes.AssignmentRoleBySubjectType["project"]>(
      "RESOURCE_VIEWER"
    );

  /** Disable the `Send Invite` button if no user has been selected and the input is not a valid email */
  const shouldSendInviteDisable =
    !selectedMembers.length && !isMemberInputValid;

  const companyMemberIdentities = useMemo(
    () =>
      new Set(companyMembers.map((companyMember) => companyMember.identity)),
    [companyMembers]
  );

  const projectMemberMap = useMemo(() => {
    const map = new Map(
      projectMembers.map((member) => [member.identity, member])
    );
    return map;
  }, [projectMembers]);

  /** Reforming company members data for stellar autocomplete component */
  const memberOptions: AutoCompleteMemberOption[] = useMemo(
    () =>
      companyMembers.map((member) => {
        const alreadyMemberInProject = projectMemberMap.get(member.identity);
        const isAlreadyMemberInProject = !!alreadyMemberInProject;

        return createMemberOption({
          member,
          isDisabled: isAlreadyMemberInProject,
          disabledMessage: isAlreadyMemberInProject
            ? `Already in project ${
                alreadyMemberInProject?.role
                  ? `as ${RoleDisplayNames[alreadyMemberInProject?.role]}`
                  : ""
              }`
            : "",
        });
      }),
    [companyMembers, projectMemberMap]
  );

  /** Reforming team data for stellar autocomplete component */
  const teamOptions: AutoCompleteTeamOption[] = useMemo(
    () =>
      teams.map((team) => {
        const alreadyTeamInProject = projectMemberMap.get(team.id);
        const isAlreadyTeamInProject = !!alreadyTeamInProject;

        return createTeamOption({
          team,
          isDisabled: isAlreadyTeamInProject,
          disabledMessage: isAlreadyTeamInProject
            ? `Already in project ${
                alreadyTeamInProject?.role
                  ? `as ${RoleDisplayNames[alreadyTeamInProject?.role]}`
                  : ""
              }`
            : "",
        });
      }),
    [projectMemberMap, teams]
  );

  /** Default setup when invite dialog open */
  function onOpenInviteDialog(): void {
    setMessage(undefined);
    setSelectedMembers([]);
    setIsDialogOpen(true);
  }

  function handleMemberSelect(members: string[]): void {
    const isMembersContainsEmail = members.some(EmailUtils.isValidEmail);

    // Show hints about adding or selecting an email
    if (isMembersContainsEmail) {
      setMessage({
        type: "info",
        helperText:
          "Invitation to this project will be sent to the new email addresses",
      });
    } else {
      setMessage(undefined);
    }
    setSelectedMembers(members);
  }

  /** Close the dialog and reset loading state */
  async function closeDialog(): Promise<void> {
    setIsDialogOpen(false);
    // Delay to ensure dialog closes before resetting loading state
    // This prevents duplicate invitation requests
    await delay(INVITE_DIALOG_CLOSE_DELAY_MS);
    setIsLoading(false);
  }

  /** Submit members to be invited */
  async function handleConfirm(): Promise<void> {
    setIsLoading(true);

    trackEvent({
      name: ProjectEvents.inviteMember,
      props: { numberOfMembers: selectedMembers.length, role: selectedRole },
    });

    // Replacing team names by team ids to include the identity in payload
    const identitiesWithTeam = selectedMembers.map((identity) => {
      return getTeamIdByName(identity) ?? identity;
    });

    /** Exclude already existed members in the project */
    const validMembers = identitiesWithTeam.filter(
      (member) => !isAlreadyMemberInCurrentProject(member)
    );

    const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"project"> = {
      assignments: [],
    };

    payload.assignments.push({
      role: selectedRole,
      identities: validMembers,
    });

    try {
      if (companyId && projectId && validMembers.length) {
        // Add the projectId to processing projects to start the loading spinner in the project
        dispatch(addProcessingProjects([projectId]));
        const response: FaroCoreApiTypes.AddMembersToProjectResponse =
          await faroCoreApiClient.projects.addMembersToProject({
            companyId,
            projectId,
            payload,
          });

        // Add invited member to the store. response.data.members exist when the response is either success or warning
        if (response.data.members) {
          const projectWithUpdatedMembers = {
            ...project,
            members: response.data.members,
          };

          dispatch(setOneProjects(projectWithUpdatedMembers));

          const newMemberResponse = response.data.members.find(
            (allMembers) =>
              !projectMembers.some(
                (oldMembers) => oldMembers.identity === allMembers.identity
              )
          );

          if (newMemberResponse && !isTeam(newMemberResponse)) {
            const isAlreadyExistsInWorkspace = companyMemberIdentities.has(
              newMemberResponse.identity
            );

            if (!isAlreadyExistsInWorkspace) {
              const newMember = convertToMemberTypes(newMemberResponse);

              /**
               * The new member should be added to the member store as well.
               * We should use the setOne because we are updating the company role for all members
               * that are present in this list for a Project Role and we should have a Company Role.
               * If we update the role to project in the members store, we will not be able to remove
               * these members from the member page. The user needs to reload the page to have the company
               * role again.
               */
              dispatch(setOne(newMember));
            }
          }
        }

        // Remove projectId from processing project to stop the loading spinner in the project
        dispatch(removeProcessingProjects([projectId]));

        respondToUser(response, validMembers.length);
      }

      void closeDialog();
    } catch (error) {
      setIsLoading(false);

      handleErrorWithToast({
        id: `inviteMember-${Date.now().toString()}`,
        title: "Cannot invite the member",
        error,
      });
    }
  }

  /** Return true if the new email is valid and it doesn't exist in the project */
  function isNewEmailValid(email: string): boolean {
    return (
      EmailUtils.isValidEmail(email) && !isAlreadyMemberInCurrentProject(email)
    );
  }

  /** Return true if member already exist with same email in the project */
  function isAlreadyMemberInCurrentProject(email: string): boolean {
    return projectMembers.some(
      (member) => member.email === email || member.identity === email
    );
  }

  function onInputChange(value: string): void {
    const isValueExistOnMemberList = memberOptions.some(({ label }) =>
      label.includes(value)
    );

    if (!value) {
      // Removing message on clearing the search text
      setMessage(undefined);
      setIsMemberInputValid(false);
    } else if (!isValueExistOnMemberList && !EmailUtils.isValidEmail(value)) {
      // Showing error message for new and invalid email
      setMessage({
        type: "error",
        helperText: "Doesn’t match any team, member.",
      });
      setIsMemberInputValid(false);
    } else if (isAlreadyMemberInCurrentProject(value)) {
      // Showing info message if member already exist in the project
      setMessage({
        type: "info",
        helperText:
          "A member with the same email already exists in the project",
      });
      setIsMemberInputValid(false);
    } else {
      if (EmailUtils.isValidEmail(value)) {
        setIsMemberInputValid(true);
      }
      setMessage(undefined);
    }
  }

  return (
    <>
      <FaroButtonContained onClick={onOpenInviteDialog}>
        Invite members
      </FaroButtonContained>
      <Dialog
        title="Invite members to project"
        confirmText="Send Invite"
        open={isDialogOpen}
        onConfirm={handleConfirm}
        showSpinner={isLoading}
        isConfirmDisabled={shouldSendInviteDisable}
        onCancel={() => setIsDialogOpen(false)}
        dataTestId="invite-members-to-project-dialog"
      >
        <Stack marginBottom={SPACE_ELEMENTS_OF_MODAL}>
          <ProjectRoleAutoComplete
            isRequired={true}
            selectedRole={selectedRole}
            onChange={(role) => setSelectedRole(role)}
          />
        </Stack>
        <MembersAutocomplete
          options={[...teamOptions, ...memberOptions]}
          handleChange={handleMemberSelect}
          onInputChange={onInputChange}
          validateNewOption={isNewEmailValid}
          isTeamName={isTeamName}
          message={message}
          labelTitle="Members"
          hasAutoFocus={true}
          placeHolder={`Email(s), ${TEAM_DISPLAY_NAME}(s) separated by comma`}
        />
      </Dialog>
    </>
  );
}
